import _ from 'lodash';
import {
  UpdateEventListener,
  FireableUpdateEventListener
} from 'utils/UpdateEventListener';
import {
  DefaultCreativeManager,
  CreativeManager
} from 'core/creative/CreativeManager';
import {
  CreativeOfCampaign,
  CreativeType,
  creativeTypeOrder,
  isOneForAllCreativeType
} from 'core/creative/Creative';
import moment from 'moment';
import { CreativeBindData } from 'core/rtbCampaign/CreativeBindData';
import { CreativeManagementStateContext } from './CreativeManagementStateContext';
import i18n from 'i18n';
import {
  isEpaperChannelGroup,
  isEpaperSpaceQuotaFull
} from 'core/channelGroup/EpaperChannelGroup';

export interface BindCreativeStateContentModel {
  readonly state: BindCreativeStateContentState;
  readonly event: UpdateEventListener<BindCreativeStateContentModel>;
  readonly creatives: CreativeOfCampaign[];
  readonly creativeTypes: number[];
  readonly stateContext?: CreativeManagementStateContext;
  readonly selectedCreatives: number[];
  readonly singleSelect: boolean;
  /**
   * 這個活動的銷售渠道是不是「一次只能綁一份素材」。
   *
   * 跟 CreativeOverviewStateContentModel.onlyOneActiveCreative 是同一條限制的另一半：
   * 那邊擋的是「已經有啟用中的素材就別再綁」，這邊擋的是「一次別綁進好幾份」。綁進來
   * 的素材預設就是啟用的（CreativeWebService 的 isActiveBinding 預設 true），少了這邊
   * 只要活動目前沒有啟用中的素材，一次勾三份還是會變成三份啟用中。
   */
  readonly onlyOneCreative: boolean;
  /**
   * 要綁進去的活動裡，有沒有已經啟用中的素材。
   *
   * 「批次編輯 > 素材 > 綁定素材」不會經過 CreativeOverviewStateContent，
   * 是 initBindStates 直接開在綁定畫面，那顆按鈕上的 canBind 完全跑不到。
   * 沒有這個旗標，活動已經有一份啟用中的素材時還是能從批次編輯再綁一份進去。
   */
  readonly hasActiveCreative: boolean;
  /**
   * 要綁進去的活動裡，電子紙機台名額已經滿的那幾檔的名稱。
   *
   * 與 hasActiveCreative 同一個理由存在於這裡：「批次編輯 > 素材 > 綁定素材」直接開在
   * 綁定畫面，總覽頁那顆按鈕上的 canBind 一次都跑不到。複選時只要有一檔滿了就整批擋掉
   * ——綁定是綁進所有選到的活動，放行等於讓那一檔綁進一份永遠投不出去的素材。
   *
   * 回名稱而不是布林：這個畫面不能取消勾選活動，只講「有活動滿了」的話，選了 20 檔的人
   * 沒有辦法知道要回列表拿掉哪一檔。
   */
  readonly quotaFullCampaignNames: string[];
  init(): Promise<void>;
  handleOnTypeFilter(type: number): void;
  handleOnSelectedFilter(): void;
  handleOnSearch(keyWord: string): void;
  handleOnSelect(creativeId: number): void;
  handleOnSelectAll(): void;
  handleOnSort(fromNewToOld: boolean): void;
  setStateContext(stateContext: CreativeManagementStateContext): void;
  reset(): void;
  getCreativeTypeLabel(creativeType: number): string;
}

export type BindCreativeStateContentProps = {
  readonly model: BindCreativeStateContentModel;
};

export type BindCreativeStateContentState = {
  readonly loading: boolean;
  readonly filteredCreatives: CreativeOfCampaign[];
  readonly selectedCreatives: number[];
  readonly selectedFilteredCreatives: number[];
  readonly selectedType?: CreativeType;
  readonly sortFromNewToOld: boolean;
};

export class DefaultBindCreativeStateContentModel implements BindCreativeStateContentModel {
  event: FireableUpdateEventListener<BindCreativeStateContentModel>;
  loading: boolean;
  manager: CreativeManager;
  creatives: CreativeOfCampaign[];
  adType?: string;
  selectedCreatives: number[];
  searchString: string;
  filteredCreatives: CreativeOfCampaign[];
  selectedType?: number;
  creativeTypes: number[];
  advertiserId: number;
  sortFromNewToOld: boolean;
  forbidCreativeIdList: number[];
  selectedFilteredCreatives: number[];
  campaigns: CreativeBindData[];
  stateContext?: CreativeManagementStateContext;
  supportScheduledCreatives: boolean;
  channelGroupId?: number;
  adTypeId?: number;
  channel: string;

  constructor(
    campaigns: CreativeBindData[],
    advertiserId: number,
    forbidCreativeIdList: number[] = [],
    public singleSelect: boolean = false,
    manager: CreativeManager = new DefaultCreativeManager(),
    public hasActiveCreative: boolean = false
  ) {
    this.event =
      new FireableUpdateEventListener<BindCreativeStateContentModel>();
    this.loading = true;
    this.manager = manager;
    this.creatives = [];
    this.campaigns = campaigns;
    this.adType = campaigns[0].adType;
    this.adTypeId = campaigns[0].adTypeId;
    this.channel = campaigns[0].channel;
    this.channelGroupId = campaigns[0].channelGroupId;
    this.selectedCreatives = [];
    this.selectedFilteredCreatives = [];
    this.searchString = '';
    this.filteredCreatives = this.creatives;
    this.creativeTypes = [];
    this.advertiserId = advertiserId;
    this.sortFromNewToOld = true;
    this.forbidCreativeIdList = forbidCreativeIdList;
    this.supportScheduledCreatives = this.campaigns.every(
      (campaign) => !campaign.isPmp
    );
  }

  reset() {
    this.searchString = '';
    this.selectedCreatives = [];
    this.selectedFilteredCreatives = [];
    this.sortFromNewToOld = true;
    this.selectedType = this.creativeTypes[0];
    this.handleOnSort(true);
  }

  get state(): BindCreativeStateContentState {
    return {
      loading: this.loading,
      selectedCreatives: this.selectedCreatives,
      filteredCreatives: this.filteredCreatives,
      selectedType: this.selectedType,
      sortFromNewToOld: this.sortFromNewToOld,
      selectedFilteredCreatives: this.selectedFilteredCreatives
    };
  }

  get onlyOneCreative(): boolean {
    return isEpaperChannelGroup(this.channelGroupId);
  }

  get quotaFullCampaignNames(): string[] {
    // 去重：活動名沒有唯一性約束，兩檔同名又都滿的話提示會變成「活動 A, A 鎖定的機台…」。
    return _.uniq(
      this.campaigns
        .filter(isEpaperSpaceQuotaFull)
        .map((campaign) => campaign.name)
    );
  }

  setStateContext = (stateContext: CreativeManagementStateContext) => {
    this.stateContext = stateContext;
  };

  getCreatives(): Promise<CreativeOfCampaign[]> {
    return this.manager.getWatingBindCreatives({
      channel: this.channel,
      channelGroupId: this.channelGroupId,
      advertiserId: this.advertiserId,
      adType: this.adType === 'PMP' ? undefined : this.adType,
      adTypeId: this.adTypeId,
      supportSchedule: this.supportScheduledCreatives
    });
  }

  getCreativeTypeLabel(creativeType: number): string {
    if (CreativeType[creativeType]) {
      return i18n.t(
        `bindCreativeStateContent.labels.${CreativeType[creativeType].toLowerCase()}`
      );
    }
    return this.manager.getCreativeTypeLabel(creativeType);
  }

  async init(): Promise<void> {
    if (this.creatives.length > 0) {
      return;
    }

    this.updateState(true);
    try {
      const creatives = await this.getCreatives();
      this.creatives = creatives.filter(
        (creative) =>
          this.forbidCreativeIdList.indexOf(creative.id) < 0 &&
          !isOneForAllCreativeType(creative.creativeType)
      );
      this.filteredCreatives = this.creatives;
      this.creatives.forEach((creative) => {
        if (this.creativeTypes.indexOf(creative.creativeType) < 0) {
          this.creativeTypes.push(creative.creativeType);
        }
      });
      this.creativeTypes = this.creativeTypes.sort((type1, type2) =>
        creativeTypeOrder.indexOf(type1) > creativeTypeOrder.indexOf(type2)
          ? 1
          : -1
      );
      if (this.creativeTypes.length > 0) {
        this.selectedType = this.creativeTypes[0];
      }

      this.handleOnSort(true);
    } catch (e) {
      console.log(`failed to load creatives of adType: ${this.adType}`, e);
      this.creatives = [];
      this.filteredCreatives = [];
      this.creativeTypes = [];
      this.updateState(false);
    }
  }

  handleOnSearch = (campaignName) => {
    this.searchString = campaignName;
    this.updateFilteredCreative();
  };

  handleOnTypeFilter = (type) => {
    this.selectedType = type;
    this.updateFilteredCreative();
  };

  handleOnSelectedFilter = () => {
    this.selectedType = undefined;
    this.updateFilteredCreative();
  };

  handleOnSort = (fromNewToOld: boolean) => {
    this.sortFromNewToOld = fromNewToOld;
    this.creatives.sort(
      (creative1: CreativeOfCampaign, creative2: CreativeOfCampaign) => {
        if (
          moment(creative1.createTime).isAfter(moment(creative2.createTime))
        ) {
          return this.sortFromNewToOld ? -1 : 1;
        }
        return this.sortFromNewToOld ? 1 : -1;
      }
    );

    this.updateFilteredCreative();
  };

  updateFilteredCreative = () => {
    this.filteredCreatives = _.filter(this.creatives, (creative) => {
      const selectedStateIsMatch =
        this.selectedType === undefined
          ? this.selectedCreatives.indexOf(creative.id) >= 0
          : true;
      const searchIsMatch =
        creative.name.includes(this.searchString) ||
        creative.id.toString().includes(this.searchString);
      const typeIsMatch =
        this.selectedType === undefined ||
        creative.creativeType === this.selectedType;
      return selectedStateIsMatch && searchIsMatch && typeIsMatch;
    });

    this.selectedFilteredCreatives = _.filter(
      this.filteredCreatives,
      (filteredCreative) =>
        this.selectedCreatives.indexOf(filteredCreative.id) > -1
    ).map((creative) => creative.id);

    this.updateState(false);
  };

  handleOnSelect = (creativeId) => {
    if (this.singleSelect) {
      this.selectedCreatives = [creativeId];
    } else {
      if (this.selectedCreatives.indexOf(creativeId) > -1) {
        _.remove(this.selectedCreatives, (id) => id === creativeId);
      } else {
        this.selectedCreatives.push(creativeId);
      }
    }

    this.updateStateData();
    this.updateFilteredCreative();
  };

  handleOnSelectAll = () => {
    const currentCreatives = _.filter(this.creatives, (creative) => {
      const searchIsMatch =
        creative.name.includes(this.searchString) ||
        creative.id.toString().includes(this.searchString);
      const typeIsMatch =
        this.selectedType === undefined ||
        creative.creativeType === this.selectedType;
      return searchIsMatch && typeIsMatch;
    }).map((creative) => creative.id);
    const selectedCreatives = _.intersection(
      this.selectedCreatives,
      currentCreatives
    );

    if (selectedCreatives.length !== currentCreatives.length) {
      this.selectedCreatives = _.uniq(
        _.concat(this.selectedCreatives, currentCreatives)
      );
    } else {
      _.remove(
        this.selectedCreatives,
        (selectedCreativeId) =>
          selectedCreatives.indexOf(selectedCreativeId) > -1
      );
    }

    this.updateStateData();
    this.updateFilteredCreative();
  };

  updateStateData = () => {
    this.stateContext?.setData &&
      this.stateContext.setData({
        activate: undefined,
        selectedCreatives: this.selectedCreatives,
        campaignNewCreativesMap: this.campaigns.map((campaign) => {
          return {
            id: campaign.id,
            draftId: campaign.draftId,
            name: campaign.name,
            creatives: _.filter(
              this.creatives,
              (creative) => this.selectedCreatives.indexOf(creative.id) >= 0
            )
          };
        })
      });
  };

  updateState(loading: boolean) {
    this.loading = loading;
    this.event.fireEvent(this);
  }
}
